import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Sentry from '@sentry/react-native';
import { AuthError, Session, User } from '@supabase/supabase-js';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Linking, Platform } from 'react-native';
import { getMembership } from '../services/get-membership.service';
import { mixpanelService } from '../services/mixpanel.service';
import type { MembershipTier } from '../types/live-session';
import { isAnonAllowedPath } from './anonAccess';
import { googleAuth } from './auth/GoogleAuth';
import { logger } from './logger';
import { fetchUserPreferences } from '../services/user-preferences.service';
import { supabase } from './supabase';
import { mapMembershipToTier } from './membershipTier';
import { invalidateMembershipQueries } from './membershipRefresh';
import {
  parsePreferenceMetadata,
  resolveMembershipSource,
  resolvePreferenceMetadata,
  type UserPreferenceMetadata,
} from './userPreferences';
import { handleSSOTokenExchange, ssoDebug } from './wordpress-redirect';
import { wpLoginSync, wpRegister } from './wpAppAuth';
import {
  configureRevenueCat,
  identifyRevenueCatUser,
  resetRevenueCatUser,
} from './purchases/RevenueCat';
import { unregisterPushNotifications } from './pushNotifications';
import { readPersistedSession } from './offline/offlineSession';
import { resolveUserDisplayName } from './userDisplayName';

/** `undefined` = not loaded yet; `null` = no row; object = preferences row exists. */
type PreferencesRowState = { metadata: UserPreferenceMetadata } | null | undefined;

/**
 * Build an AuthError-shaped object from an edge-function error so the auth
 * screens (which only read `.message`) can display it uniformly.
 */
function makeAuthError(message: string, status?: number): AuthError {
  const err = new Error(message) as AuthError;
  err.name = 'AuthError';
  if (typeof status === 'number') {
    (err as { status?: number }).status = status;
  }
  return err;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  /** Instrument / level from user_preferences, with auth metadata fallback until a row exists. */
  preferenceMetadata: UserPreferenceMetadata | null;
  preferencesLoading: boolean;
  refreshUserPreferences: () => Promise<void>;
  applyPreferenceMetadata: (metadata: UserPreferenceMetadata) => void;
  membershipTier: MembershipTier;
  membershipLoading: boolean;
  /** Re-sync the membership tier (e.g. after an in-app purchase). */
  refreshMembership: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<{ error?: AuthError | null }>;
  signUp: (email: string, password: string, metadata?: any) => Promise<{ error?: AuthError | null }>;
  signOut: () => Promise<{ error?: AuthError | null }>;
  signInWithGoogle: () => Promise<{ error?: AuthError | Error | null }>;
  deleteAccount: () => Promise<{ error?: AuthError | null }>;
  updateProfile: (updates: any) => Promise<{ error?: AuthError | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [membershipTier, setMembershipTier] = useState<MembershipTier>('non_member');
  const [membershipLoading, setMembershipLoading] = useState(true);
  const [preferencesRow, setPreferencesRow] = useState<PreferencesRowState>(undefined);
  const [preferencesLoading, setPreferencesLoading] = useState(false);
  const lastTrackedSignInUserIdRef = useRef<string | null>(null);

  const preferenceMetadata = useMemo(() => {
    if (!user) return null;
    return resolvePreferenceMetadata(
      preferencesRow === undefined ? undefined : preferencesRow,
      user.user_metadata as UserPreferenceMetadata | undefined,
    );
  }, [user, preferencesRow]);

  const refreshUserPreferences = useCallback(async () => {
    if (!user?.id) {
      setPreferencesRow(undefined);
      return;
    }
    setPreferencesLoading(true);
    try {
      const row = await fetchUserPreferences(user.id);
      setPreferencesRow(row);
    } catch (error) {
      logger.error('Error loading user_preferences:', error);
      setPreferencesRow(null);
    } finally {
      setPreferencesLoading(false);
    }
  }, [user?.id]);

  // The RevenueCat webhook writes the upgraded tier into user_preferences (and
  // auth metadata) within seconds of a purchase; re-reading preferences lets the
  // membership effect below resolve the new tier without a full app reload.
  const refreshMembership = useCallback(async () => {
    await refreshUserPreferences();
  }, [refreshUserPreferences]);

  const applyPreferenceMetadata = useCallback(
    (patch: UserPreferenceMetadata) => {
      setPreferencesRow((prev) => {
        const authBase = parsePreferenceMetadata(
          user?.user_metadata as UserPreferenceMetadata | undefined,
        );
        const existing =
          prev === undefined || prev === null ? authBase : prev.metadata;
        return {
          metadata: { ...existing, ...parsePreferenceMetadata(patch) },
        };
      });
    },
    [user?.user_metadata],
  );

  useEffect(() => {
    if (!user?.id) {
      setPreferencesRow(undefined);
      setPreferencesLoading(false);
      return;
    }
    void refreshUserPreferences();
  }, [user?.id, refreshUserPreferences]);


  useEffect(() => {
    const getInitialSession = async () => {
      let resolvedSession: Session | null = null;
      let getSessionCompleted = false;
      try {
        let timeoutId: ReturnType<typeof setTimeout> | null = null;
        const timeoutPromise = new Promise<never>((_, reject) => {
          timeoutId = setTimeout(() => {
            logger.warn('[Auth] getSession() timed out after 8s', { platform: Platform.OS });
            Sentry.addBreadcrumb({ category: 'auth', message: 'getSession timeout — loading will unblock', level: 'warning' });
            reject(new Error('getSession timeout'));
          }, 8000);
        });
        try {
          const { data: { session }, error } = await Promise.race([supabase.auth.getSession(), timeoutPromise]);
          getSessionCompleted = true;
          if (error) {
            // Offline with an expired access token: supabase-js cannot refresh
            // and returns no session, which would bounce the user to login.
            // Keep them signed in from the persisted session; the refresh
            // token is intact and supabase-js refreshes once back online.
            const persisted = Platform.OS !== 'web' ? await readPersistedSession() : null;
            if (persisted) {
              logger.warn('[Auth] getSession failed, using persisted session (offline?)', { message: error.message });
              resolvedSession = persisted;
              setSession(persisted);
              setUser(persisted.user);
            } else {
              logger.error('Error getting session:', error);
            }
          } else if (session) {
            resolvedSession = session;
            setSession(session);
            setUser(session?.user || null);
          }
        } finally {
          if (timeoutId) clearTimeout(timeoutId);
        }
      } catch (error) {
        logger.error('Error in getInitialSession:', error);
      } finally {
        // On the web, if getSession() completed and returned no session, and
        // we're on an anon-allowed route, create an anonymous Supabase session.
        // We deliberately skip this on timeout: a real session might still be
        // loading from storage, and we don't want to overwrite it with an anon.
        if (
          getSessionCompleted &&
          !resolvedSession &&
          Platform.OS === 'web' &&
          typeof window !== 'undefined' &&
          isAnonAllowedPath(window.location.pathname)
        ) {
          try {
            const { error: anonError } = await supabase.auth.signInAnonymously();
            if (anonError) {
              logger.error('Anonymous sign-in failed:', anonError);
            }
          } catch (anonErr) {
            logger.error('Unexpected error during anonymous sign-in:', anonErr);
          }
        }
        setLoading(false);
      }
    };

    getInitialSession();

    // Handle deep links for email confirmation and OAuth callbacks
    const handleDeepLink = async (url: string) => {
      __DEV__ && logger.debug('Deep link received:', url);

      // Check if this is an auth callback
      if (url.includes('auth/callback') || url.includes('#access_token=')) {
        try {
          // Let Supabase handle the URL
          const { 
            data, 
            // error 
          } = await supabase.auth.getSession();
          if (data?.session) {
            setSession(data.session);
            setUser(data.session.user);
          }
        } catch (error) {
          logger.error('Error handling deep link:', error);
        }
      }
      // Check if this is a WordPress SSO token (native deep link)
      else if (url.includes('sso_token=')) {
        const urlParams = new URLSearchParams(url.split('?')[1]);
        const token = urlParams.get('sso_token');
        if (token) {
          ssoDebug('native deep link — running SSO exchange');
          const result = await handleSSOTokenExchange(token);
          if (!result.success) {
            logger.error('Native SSO exchange failed:', result.error?.message);
          }
        }
      }
    };

    let deepLinkSubscription: { remove: () => void } | undefined;
    if (Platform.OS !== 'web') {
      deepLinkSubscription = Linking.addEventListener('url', (event) => {
        handleDeepLink(event.url);
      });

      Linking.getInitialURL().then((url) => {
        if (url) {
          handleDeepLink(url);
        }
      });
    }

    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        __DEV__ && logger.debug('Auth event:', event, session?.user?.email);

        if (event === 'SIGNED_OUT') {
          
          setSession(null);
          setUser(null);
          setPreferencesRow(undefined);
          setLoading(false);
          mixpanelService.reset();
          // Allow future "Signed In" events for this user
          lastTrackedSignInUserIdRef.current = null;
          
          // Clear any stored session data
          if (Platform.OS === 'web') {
            // Already handled in signOut function
          } else {
            await AsyncStorage.removeItem('auth_session');
          }
        }
        else if (event === 'SIGNED_IN') {
          setSession(session);
          setUser(session?.user || null);
          setLoading(false);

          // Anonymous users have no email and shouldn't pollute Mixpanel identity.
          if (session?.user?.email && !session.user.is_anonymous) {
            __DEV__ && logger.debug('Identifying user in Mixpanel:', session.user.email);
            await mixpanelService.identifyUser(session.user.email);
            // Guard: only track "Signed In" once per user/session
            if (lastTrackedSignInUserIdRef.current !== session.user.id) {
              lastTrackedSignInUserIdRef.current = session.user.id;
              mixpanelService.trackUserSignIn(session.user);
            }
          }
        }
        else if (event === 'TOKEN_REFRESHED') {
          setSession(session);
          setUser(session?.user || null);
          setLoading(false);

          if (session?.user?.email && !session.user.is_anonymous) {
            __DEV__ && logger.debug('Identifying user in Mixpanel (Token Refresh):', session.user.email);
            await mixpanelService.identifyUser(session.user.email);
          }
        }
        else if (event === 'USER_UPDATED') {
          setSession(session);
          setUser(session?.user || null);
        }
        
        // Store session info for debugging (mobile only)
        if (session && Platform.OS !== 'web') {
            await AsyncStorage.setItem('auth_session', JSON.stringify({
              access_token: session.access_token,
              refresh_token: session.refresh_token,
              expires_at: session.expires_at,
              user_id: session.user.id,
            }));
        }
      }
    );

    return () => {
      deepLinkSubscription?.remove();
      authSubscription.unsubscribe();
    };
  }, []);

  // Configure RevenueCat (Apple IAP) once on mount. No-op off iOS / without key.
  useEffect(() => {
    configureRevenueCat();
  }, []);

  // Bind the RevenueCat customer to the Supabase user id (app_user_id) so iOS
  // purchases and entitlements follow the account across devices; reset on
  // sign-out / anonymous sessions. No-op on web.
  const revenueCatUserId = user?.id;
  const revenueCatEmail = user?.email ?? null;
  const isAnonymousUser = user?.is_anonymous ?? false;
  useEffect(() => {
    if (loading) return;
    if (revenueCatUserId && !isAnonymousUser) {
      // Same priority as Account / Edit profile (display_name first): WP-imported
      // users often have only display_name, no first_name/full_name.
      const displayName = resolveUserDisplayName(user?.user_metadata) || null;
      void identifyRevenueCatUser(revenueCatUserId, revenueCatEmail, displayName);
    } else {
      void resetRevenueCatUser();
    }
  }, [loading, revenueCatUserId, revenueCatEmail, isAnonymousUser]);

  useEffect(() => {
    if (loading) return;
    if (!user || user.is_anonymous) {
      setMembershipTier('non_member');
      setMembershipLoading(false);
      return;
    }

    if (preferencesRow === undefined) {
      setMembershipLoading(true);
      return;
    }

    const membership = resolveMembershipSource(
      preferencesRow,
      user.user_metadata as UserPreferenceMetadata | undefined,
    );
    if (membership === 'pro' || membership === 'regular' || membership === 'none') {
      setMembershipTier(mapMembershipToTier(membership));
      setMembershipLoading(false);
      return;
    }

    let cancelled = false;
    setMembershipLoading(true);
    getMembership()
      .then((res) => {
        if (cancelled) return;
        const tier =
          res?.membership != null
            ? mapMembershipToTier(res.membership)
            : 'non_member';
        setMembershipTier(tier);
      })
      .catch(() => {
        if (!cancelled) setMembershipTier('non_member');
      })
      .finally(() => {
        if (!cancelled) setMembershipLoading(false);
      });
    return () => { cancelled = true; };
  }, [loading, user, preferencesRow]);

  const lastTierRef = useRef<MembershipTier | null>(null);
  useEffect(() => {
    if (membershipLoading) return;
    const previous = lastTierRef.current;
    lastTierRef.current = membershipTier;
    if (previous === null || previous === membershipTier) return;
    void invalidateMembershipQueries(user?.id);
  }, [membershipTier, membershipLoading, user?.id]);

  const signIn = async (email: string, password: string) => {
    try {
      // Fast path: a user whose Supabase password is already in sync signs in
      // directly, with no WordPress round-trip.
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (!error) {
        return { error: null };
      }

      // Supabase rejected the credentials. This is expected for a WordPress user
      // who has never logged into the app (their Supabase password is a random
      // value set by the WP→Supabase bridge). Validate against WordPress; if it
      // succeeds the edge function syncs the Supabase password and we retry.
      const sync = await wpLoginSync(email, password);
      if (!sync.ok) {
        // 401 => wrong email/password; anything else => a transient/server issue.
        return { error: makeAuthError(sync.error || error.message, sync.status) };
      }

      const retry = await supabase.auth.signInWithPassword({ email, password });
      if (retry.error) {
        logger.error('Sign in retry after WordPress sync failed:', retry.error);
      }
      return { error: retry.error };
    } catch (error) {
      logger.error('Unexpected error during sign in:', error);
      return { error: error as AuthError };
    }
  };

  const signUp = async (email: string, password: string, metadata?: any) => {
    try {
      // Create the user on WordPress + Supabase (same password) via the edge
      // function, then sign in immediately. WordPress is the system of record,
      // so we never call supabase.auth.signUp directly.
      const reg = await wpRegister({
        email,
        password,
        firstName: metadata?.first_name,
        lastName: metadata?.last_name,
      });
      if (!reg.ok) {
        logger.error('Registration failed:', reg.status, reg.error);
        return { error: makeAuthError(reg.error || 'Registration failed', reg.status) };
      }

      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        logger.error('Sign in after registration failed:', error);
      }
      return { error };
    } catch (error) {
      logger.error('Unexpected error during sign up:', error);
      return { error: error as AuthError };
    }
  };

  const signInWithGoogle = async () => {
    try {
      const { 
        // data, 
        error } = await googleAuth.signInWithGoogle();
      
      if (error) {
        logger.error('Google sign in error:', error);
      }
      
      return { error };
    } catch (error) {
      logger.error('Unexpected error during Google sign in:', error);
      return { error: error as AuthError };
    }
  };

    const signOut = async () => {
    try {
      // Remove this device's push token while the session can still pass RLS.
      await unregisterPushNotifications();

      if (session?.user?.email) {
         mixpanelService.trackUserSignOut(session.user);
         mixpanelService.reset()
      }
      setSession(null);
      setUser(null);
      

      // Clear storage manually on web to ensure session is removed
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        // Clear all Supabase-related items from localStorage
        const keysToRemove = [];
        for (let i = 0; i < window.localStorage.length; i++) {
          const key = window.localStorage.key(i);
          if (key && (key.includes('supabase') || key.includes('auth-token'))) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => window.localStorage.removeItem(key));
      }

      // Now attempt to sign out from Supabase
      const { error } = await supabase.auth.signOut();

      if (error) {
        logger.error('Sign out error:', error);
        // Ignore "Auth session missing!" error since we already cleared everything
        if (error.message === 'Auth session missing!') {
          return { error: null };
        }
      }

      return { error };
    } catch (error) {
      logger.error('Unexpected error during sign out:', error);
      return { error: error as AuthError };
    }
  };

  // Password resets intentionally have no in-app path: they go through the
  // WordPress lost-password flow (URLS.LOST_PASSWORD) and wp-login re-syncs
  // the Supabase password on the next sign-in. A Supabase-only reset here
  // would drift the two systems apart.

  const updateProfile = async (updates: any) => {
    try {
      const { data, error } = await supabase.auth.updateUser({
        data: updates,
      });

      if (error) {
        logger.error('Profile update error:', error);
        return { error };
      }

      // Keep local auth state in sync immediately — USER_UPDATED can lag or
      // be missed, which left Edit name showing stale values until refresh.
      if (data.user) {
        setUser(data.user);
        setSession((prev) =>
          prev ? { ...prev, user: data.user! } : prev,
        );
      }

      // Push name changes to the WordPress user so community/reviews/emails
      // don't drift. Non-fatal: Supabase is the app's source of truth and the
      // next successful save re-syncs.
      if (updates.first_name || updates.last_name || updates.display_name) {
        try {
          const { error: syncError } = await supabase.functions.invoke('wp-sync-profile', {
            body: {
              first_name: updates.first_name,
              last_name: updates.last_name,
              display_name: updates.display_name,
            },
          });
          if (syncError) {
            logger.warn('Profile WP sync failed (non-fatal):', syncError);
          }
        } catch (syncError) {
          logger.warn('Profile WP sync failed (non-fatal):', syncError);
        }
      }

      return { error: null };
    } catch (error) {
      logger.error('Unexpected error during profile update:', error);
      return { error: error as AuthError };
    }
  };

  const deleteAccount = async () => {
    try {
      __DEV__ && logger.debug('deleteAccount: Starting account deletion for', user?.email);

      // The delete-account Edge Function cancels website billing, anonymizes
      // the WordPress user and hard-deletes the Supabase user. It aborts
      // without deleting anything if the billing step fails.
      const { error: invokeError } = await supabase.functions.invoke('delete-account');

      if (invokeError) {
        // Non-2xx responses surface as FunctionsHttpError; the server's JSON
        // error message is on the response in `context`.
        let message = 'Could not delete your account. Please contact support.';
        try {
          const body = await (invokeError as any).context?.json?.();
          if (body?.error) message = body.error;
        } catch {
          // keep the generic message
        }
        logger.error('deleteAccount: Edge Function failed:', invokeError);
        return { error: { name: 'DeleteAccountError', message } as AuthError };
      }

      // The server-side user is gone, so only clear the local session — a
      // global sign-out would call the auth server with a dead token.
      const { error: signOutError } = await supabase.auth.signOut({ scope: 'local' });
      if (signOutError) {
        logger.error('deleteAccount: local sign out error after deletion:', signOutError);
        // Not returned: the account itself is already deleted.
      }

      __DEV__ && logger.debug('deleteAccount: Account deletion completed');
      return { error: null };
    } catch (error) {
      logger.error('deleteAccount: Unexpected error during account deletion:', error);
      return { error: error as AuthError };
    }
  };

  const value: AuthContextType = {
    user,
    session,
    loading,
    preferenceMetadata,
    preferencesLoading,
    refreshUserPreferences,
    applyPreferenceMetadata,
    membershipTier,
    membershipLoading,
    refreshMembership,
    signIn,
    signUp,
    signOut,
    signInWithGoogle,
    deleteAccount,
    updateProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
